import ISPV from "./ISPV";
import React from "react";
import AsyncImage from "@appComponents/AsyncImage/AsyncImage";
import noPhotoCompany from "@images/no_photo_company.png";
import { MAIN_CURRENCY_LABEL } from "@bundles/consts";
import { Button } from "@appComponents/Button";
import { Link } from "react-router-dom";
import ChevronDownIcon from "../../../../icons/chevron-down-black.svg";
import { SPVPriceChart } from "../SPVPriceChart";
import classNames from "classnames";

type GetSpvColumnsArgs = {
  userSignedIn: boolean;
  isMobile: boolean;
  handleExpandToggle: (record: ISPV) => void;
  expandedRowKeys: React.Key[];
};

export const getSpvColumns = ({
  userSignedIn,
  isMobile,
  handleExpandToggle,
  expandedRowKeys,
}: GetSpvColumnsArgs) =>
  isMobile
    ? [
        {
          title: "MARKET",
          dataIndex: "name",
          key: "name",
          render: (_, spv: ISPV) => {
            return (
              <div className="flex items-center gap-2">
                <AsyncImage
                  image={spv.company_avatar.url}
                  fallback={noPhotoCompany}
                >
                  <img
                    alt="logo"
                    className="h-[36px] w-[36px] rounded-[5px] border border-[#E4E4E4] rounded-[8px]"
                  />
                </AsyncImage>

                <span>{spv.display_name}</span>
              </div>
            );
          },
        },
        {
          title: "",
          dataIndex: "action",
          key: "action",
          width: 10,
          render: (_, spv: ISPV) => {
            return (
              <Button isSmall onClick={() => handleExpandToggle(spv)}>
                <span
                  className={classNames({
                    "rotate-180": expandedRowKeys.includes(spv.id),
                  })}
                >
                  <ChevronDownIcon />
                </span>
              </Button>
            );
          },
        },
      ]
    : [
        {
          title: "MARKET",
          dataIndex: "name",
          key: "name",
          sorter: (a, b) => a.name.localeCompare(b.name),
          render: (_, spv: ISPV) => {
            return (
              <div className="flex items-center gap-2">
                <AsyncImage
                  image={spv.company_avatar.url}
                  fallback={noPhotoCompany}
                >
                  <img
                    alt="logo"
                    className="h-[36px] w-[36px] rounded-[5px] border border-[#E4E4E4] rounded-[8px]"
                  />
                </AsyncImage>

                <span>{spv.display_name}</span>
              </div>
            );
          },
        },
        {
          title: "COMPANY",
          dataIndex: "company_name",
          key: "company_name",
          sorter: (a, b) => a.company_name.localeCompare(b.company_name),
          render: (text: string) => {
            return text || "-";
          },
        },
        {
          title: "LAST PRICE",
          dataIndex: "last_price",
          key: "last_price",
          sorter: (a, b) =>
            Number(a.last_trade_price ?? 0) - Number(b.last_trade_price ?? 0),
          render: (_, spv: ISPV) => {
            if (!spv.last_trade_price) {
              return "-";
            }

            return (
              <>
                {spv.last_trade_price !== 0 &&
                  spv.last_trade_price !== null &&
                  `${Number(spv.last_trade_price ?? 0).toFixed(
                    2
                  )} ${MAIN_CURRENCY_LABEL.toUpperCase()}`}
              </>
            );
          },
        },
        {
          title: "LAST 7 DAYS",
          dataIndex: "last_7d",
          key: "last_7d",
          render: (_, spv: ISPV) => {
            if (!spv.id || !spv.trading_asset_id) return "-";

            return (
              <SPVPriceChart
                spvId={spv.id}
                spvTradingAssetId={spv.trading_asset_id}
              />
            );
          },
        },
        {
          title: "",
          dataIndex: "action",
          key: "action",
          render: (_, spv: ISPV) => {
            const orderPageRoute = `/companies/${spv.company_slug}/market/spvs/${spv.id}/orders`;

            return (
              <Button
                href={
                  userSignedIn
                    ? orderPageRoute
                    : `/users/sign_in?return_url=${orderPageRoute}`
                }
                isSmall
                isPrimary
              >
                Trade
              </Button>
            );
          },
        },
      ];
